import React from "react";
import { Document, Page, Text, View, StyleSheet, Link, pdf } from "@react-pdf/renderer";
import html2pdf from "html2pdf.js";
import { config } from "../../CommonUtils/CommonURLS";
import { viewImage } from "../../CommonUtils/viewImage";

const buildDocUrl = (docPath) => {
  if (!docPath) return "";
  const img = String(docPath).includes("affiliation") ? docPath : "affiliation/" + docPath;
  return config.url.FILE_DOWNLOAD_URL + img;
};

const pdfStyles = StyleSheet.create({
  page: { padding: 20, fontSize: 8.5, fontFamily: "Helvetica" },
  header: { backgroundColor: "#1a472a", padding: 10, marginBottom: 12, textAlign: "center" },
  headerTitle: { color: "white", fontSize: 14, fontWeight: "bold" },
  headerSubtitle: { color: "#e0e0e0", fontSize: 9, marginTop: 3 },
  headerDate: { color: "#e0e0e0", fontSize: 8, marginTop: 3 },
  sectionTitle: { fontSize: 10.5, fontWeight: "bold", backgroundColor: "#f0f0f0", padding: 6, borderWidth: 1, borderColor: "#cccccc", marginTop: 10 },
  table: { width: "100%", borderWidth: 1, borderColor: "#cccccc", marginBottom: 10 },
  tableHeader: { backgroundColor: "#e8f5e9", flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#2e7d32", paddingVertical: 5 },
  tableRow: { flexDirection: "row", borderBottomWidth: 0.5, borderBottomColor: "#dddddd", paddingVertical: 4 },
  colSno: { width: "9%", paddingHorizontal: 3 },
  colParticulars: { width: "43%", paddingHorizontal: 4 },
  colValue: { width: "16%", paddingHorizontal: 4 },
  colValueWide: { width: "16%", paddingHorizontal: 4 },
  colRemarks: { width: "16%", paddingHorizontal: 4 },
  halfCell: { width: "50%", paddingHorizontal: 4 },
  summaryLabel: { fontWeight: "bold" },
  statusYes: { color: "#2e7d32", fontWeight: "bold" },
  statusNo: { color: "#d32f2f", fontWeight: "bold" },
  smallText: { fontSize: 7.5 },
  docLink: { color: "#1a73e8", fontSize: 8, textDecoration: "underline" },
  footer: { marginTop: 14, textAlign: "center", fontSize: 7.5, color: "#666666", borderTopWidth: 1, borderTopColor: "#cccccc", paddingTop: 8 },
});

const getValue = (source = {}, ...keys) => {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "";
};

const formatPlain = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  if (value === true) {
    return "Yes";
  }

  if (value === false) {
    return "No";
  }

  return String(value);
};

const formatYesNo = (value) => {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  const normalized = String(value).trim().toLowerCase();
  if (["yes", "y", "true"].includes(normalized)) {
    return "Yes";
  }
  if (["no", "n", "false"].includes(normalized)) {
    return "No";
  }

  return String(value);
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return formatPlain(value);
  }

  return date.toLocaleDateString("en-GB");
};

const normalizeStatusCell = (value) => {
  const normalized = formatYesNo(value);
  if (normalized === "Yes") {
    return { value: normalized, style: pdfStyles.statusYes };
  }
  if (normalized === "No") {
    return { value: normalized, style: pdfStyles.statusNo };
  }
  return { value: normalized, style: null };
};

const getAccommodationKey = (row = {}) => {
  const nameKey = String(row?.particularName || row?.particular_name || "")
    .trim()
    .toLowerCase();
  if (nameKey) {
    return `name:${nameKey}`;
  }

  const codeKey = String(row?.particularCode || row?.particular_code || "").trim();
  if (codeKey) {
    return `code:${codeKey}`;
  }

  return "";
};

const getPdfField = (values = {}, ...keys) => getValue(values, ...keys);

const renderSummaryTable = (title, items = []) => {
  const validItems = items.filter((item) => item && (item.label || item.value !== undefined));
  if (!validItems.length) {
    return null;
  }

  const rows = [];
  for (let index = 0; index < validItems.length; index += 2) {
    rows.push(validItems.slice(index, index + 2));
  }

  return (
    <React.Fragment key={title}>
      <Text style={pdfStyles.sectionTitle}>{title}</Text>
      <View style={pdfStyles.table}>
        {rows.map((row, index) => (
          <View key={`${title}-${index}`} style={pdfStyles.tableRow}>
            <Text style={pdfStyles.halfCell}>{`${row[0]?.label || ""}: ${formatPlain(row[0]?.value)}`}</Text>
            <Text style={pdfStyles.halfCell}>{row[1] ? `${row[1].label}: ${formatPlain(row[1].value)}` : ""}</Text>
          </View>
        ))}
      </View>
    </React.Fragment>
  );
};

const SectionComparisonTable = ({
  title,
  rows = [],
  particularsLabel = "Particulars",
  existingLabel = "Existing / Handing Over",
  proposedLabel = "Proposed / Taking Over",
  remarksLabel = "Remarks of the Scrutiny Committee",
}) => {
  if (!rows.length) {
    return null;
  }

  return (
    <React.Fragment key={title}>
      <Text style={pdfStyles.sectionTitle}>{title}</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={pdfStyles.colSno}>S. No.</Text>
          <Text style={pdfStyles.colParticulars}>{particularsLabel}</Text>
          <Text style={pdfStyles.colValue}>{existingLabel}</Text>
          <Text style={pdfStyles.colValueWide}>{proposedLabel}</Text>
          <Text style={pdfStyles.colRemarks}>{remarksLabel}</Text>
        </View>
        {rows.map((row, index) => {
          const existingStatus = row.yesNo ? normalizeStatusCell(row.existing) : { value: formatPlain(row.existing), style: null };
          const proposedStatus = row.yesNo ? normalizeStatusCell(row.proposed) : { value: formatPlain(row.proposed), style: null };

          return (
            <View key={`${title}-${index}`} style={pdfStyles.tableRow}>
              <Text style={pdfStyles.colSno}>{row.sno || index + 1}</Text>
              <Text style={pdfStyles.colParticulars}>{row.label}</Text>
              <Text style={[pdfStyles.colValue, existingStatus.style]}>{existingStatus.value}</Text>
              <View style={pdfStyles.colValueWide}>
                {row.docUrl ? (
                  <Link src={row.docUrl} style={pdfStyles.docLink}>
                    View Document
                  </Link>
                ) : (
                  <Text style={proposedStatus.style}>{proposedStatus.value}</Text>
                )}
              </View>
              <Text style={pdfStyles.colRemarks}>{formatPlain(row.remarks)}</Text>
            </View>
          );
        })}
      </View>
    </React.Fragment>
  );
};

const SimpleChecklistTable = ({ title, rows = [], particularsLabel = "Details", valueLabel = "Please tick", remarksLabel = "Remarks of the Scrutiny Committee" }) => {
  if (!rows.length) {
    return null;
  }

  return (
    <React.Fragment key={title}>
      <Text style={pdfStyles.sectionTitle}>{title}</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={pdfStyles.colSno}>S.No.</Text>
          <Text style={pdfStyles.colParticulars}>{particularsLabel}</Text>
          <Text style={pdfStyles.colValue}>{valueLabel}</Text>
          <Text style={{ width: "32%", paddingHorizontal: 4 }}>{remarksLabel}</Text>
        </View>
        {rows.map((row, index) => {
          const statusCell = row.yesNo ? normalizeStatusCell(row.value) : { value: formatPlain(row.value), style: null };

          return (
            <View key={`${title}-${index}`} style={pdfStyles.tableRow}>
              <Text style={pdfStyles.colSno}>{row.sno || index + 1}</Text>
              <Text style={pdfStyles.colParticulars}>{row.label}</Text>
              <View style={pdfStyles.colValue}>
                {row.docUrl ? (
                  <Link src={row.docUrl} style={pdfStyles.docLink}>
                    View Document
                  </Link>
                ) : (
                  <Text style={statusCell.style}>{statusCell.value}</Text>
                )}
              </View>
              <Text style={{ width: "32%", paddingHorizontal: 4 }}>{formatPlain(row.remarks)}</Text>
            </View>
          );
        })}
      </View>
    </React.Fragment>
  );
};

const AccommodationTable = ({ rows = [], baseRows = [] }) => {
  const tableRows =
    Array.isArray(rows) && rows.length > 0
      ? rows
      : Array.isArray(baseRows)
        ? baseRows.map((item) => ({
            particularName: item.particular_name,
            particularCode: item.particular_code,
            sufficientAsPerNorms: "",
            remarks: "",
            no_of_rooms: item.no_of_rooms,
            total_area: item.total_area,
          }))
        : [];

  if (!tableRows.length) {
    return null;
  }

  return (
    <React.Fragment key="Accommodation Details">
      <Text style={pdfStyles.sectionTitle}>4.18. Accommodation Details</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={{ width: "8%", paddingHorizontal: 4 }}>Sl. No.</Text>
          <Text style={{ width: "24%", paddingHorizontal: 4 }}>Type of the room</Text>
          <Text style={{ width: "10%", paddingHorizontal: 4 }}>No. of room(s) required</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Total carpet area (sft) required</Text>
          <Text style={{ width: "10%", paddingHorizontal: 4 }}>No. of room(s) provided</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Total carpet area (sft) provided</Text>
          <Text style={{ width: "10%", paddingHorizontal: 4 }}>Whether sufficient as per norms</Text>
          <Text style={{ width: "18%", paddingHorizontal: 4 }}>Remarks</Text>
        </View>
        {tableRows.map((row, index) => {
          const statusCell = normalizeStatusCell(row?.sufficientAsPerNorms || row?.sufficient_as_per_norms || row?.sufficient_orms);
          const rowKey = getAccommodationKey(row);
          const baseRow = baseRows.find((item) => getAccommodationKey(item) === rowKey) || baseRows[index] || {};

          return (
            <View key={`accom-${index}`} style={pdfStyles.tableRow}>
              <Text style={{ width: "8%", paddingHorizontal: 4 }}>{index + 1}</Text>
              <Text style={{ width: "24%", paddingHorizontal: 4 }}>{formatPlain(row?.particularName || row?.particular_name || baseRow?.particular_name)}</Text>
              <Text style={{ width: "10%", paddingHorizontal: 4 }}>{formatPlain(baseRow?.no_of_rooms || row?.no_of_rooms)}</Text>
              <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatPlain(baseRow?.total_area || row?.total_area)}</Text>
              <Text style={{ width: "10%", paddingHorizontal: 4 }}>{formatPlain(baseRow?.no_of_rooms || row?.no_of_rooms)}</Text>
              <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatPlain(baseRow?.total_area || row?.total_area)}</Text>
              <Text style={[{ width: "10%", paddingHorizontal: 4 }, statusCell.style]}>{statusCell.value}</Text>
              <Text style={{ width: "18%", paddingHorizontal: 4 }}>{formatPlain(row?.remarks)}</Text>
            </View>
          );
        })}
      </View>
    </React.Fragment>
  );
};

const CorpusFundChecklistTable = ({ values = {} }) => {
  const corpusFund = getPdfField(values, "corpusFund", "corpus_fund", "corpus_fund_sufficient");
  const certificateIssued = getPdfField(values, "certificateIssued", "certificate_issued");
  const rows = [
    {
      sno: "a.",
      label: "Whether the college was exempted from paying the Corpus Fund:",
      value: corpusFund,
      remarks: "",
      yesNo: true,
    },
  ];

  if (String(corpusFund).trim() === "Yes") {
    rows.push({
      sno: "b.",
      label: "whether the rural/tribal area certificate issued by MRO is produced:",
      value: certificateIssued,
      remarks: "",
      yesNo: true,
    });
  }

  return <SimpleChecklistTable title="7. Corpus Fund" rows={rows} particularsLabel="Particulars" valueLabel="Action" remarksLabel="" />;
};

const CorpusFundsTable = ({ title, rows = [] }) => {
  if (!rows.length) {
    return null;
  }

  return (
    <React.Fragment key={title}>
      <Text style={pdfStyles.sectionTitle}>{title}</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={{ width: "8%", paddingHorizontal: 4 }}>Sl.</Text>
          <Text style={{ width: "20%", paddingHorizontal: 4 }}>Programme</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Amount</Text>
          <Text style={{ width: "16%", paddingHorizontal: 4 }}>Period</Text>
          <Text style={{ width: "16%", paddingHorizontal: 4 }}>FDR / Bank No.</Text>
          <Text style={{ width: "16%", paddingHorizontal: 4 }}>Bank / IFSC</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Maturity</Text>
        </View>
        {rows.map((item, index) => (
          <View key={`${title}-${index}`} style={pdfStyles.tableRow}>
            <Text style={{ width: "8%", paddingHorizontal: 4 }}>{index + 1}</Text>
            <Text style={{ width: "20%", paddingHorizontal: 4 }}>{formatPlain(item.course_name || item.programme)}</Text>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatPlain(item.amount)}</Text>
            <Text style={{ width: "16%", paddingHorizontal: 4 }}>{`${formatDate(item.period_from)} - ${formatDate(item.period_to)}`}</Text>
            <Text style={{ width: "16%", paddingHorizontal: 4 }}>{formatPlain(item.fdr_bank_number || item.fdr_no)}</Text>
            <Text style={{ width: "16%", paddingHorizontal: 4 }}>
              {formatPlain(item.bank_address || item.bank_name)} {item.ifsc ? `(${item.ifsc})` : item.ifsc_code ? `(${item.ifsc_code})` : ""}
            </Text>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatDate(item.date_maturity || item.maturity_date)}</Text>
          </View>
        ))}
      </View>
    </React.Fragment>
  );
};

const ProgrammesTable = ({ title, rows = [], typeLabel }) => {
  if (!rows.length) {
    return null;
  }

  return (
    <React.Fragment key={title}>
      <Text style={pdfStyles.sectionTitle}>{title}</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Sl. No.</Text>
          <Text style={{ width: "48%", paddingHorizontal: 4 }}>{typeLabel}</Text>
          <Text style={{ width: "20%", paddingHorizontal: 4 }}>Total No. of programmes</Text>
          <Text style={{ width: "20%", paddingHorizontal: 4 }}>Sanctioned Intake</Text>
        </View>
        {rows.map((item, index) => (
          <View key={`${title}-${index}`} style={pdfStyles.tableRow}>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{index + 1}</Text>
            <Text style={{ width: "48%", paddingHorizontal: 4 }}>{formatPlain(item.course_cat_name || item.course_category_name || item.programme || item.course_name)}</Text>
            <Text style={{ width: "20%", paddingHorizontal: 4 }}>{formatPlain(item.count ?? item.total_programmes ?? item.total_count)}</Text>
            <Text style={{ width: "20%", paddingHorizontal: 4 }}>{formatPlain(item.sum ?? item.sanctioned_intake ?? item.intake)}</Text>
          </View>
        ))}
      </View>
    </React.Fragment>
  );
};

const InstallmentTable = ({ rows = [], title = "For Degree Colleges established before 2021-22" }) => {
  if (!rows.length) {
    return null;
  }

  return (
    <React.Fragment key="Corpus Fund Installments">
      <Text style={pdfStyles.sectionTitle}>{title}</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={{ width: "10%", paddingHorizontal: 4 }}>Sl.</Text>
          <Text style={{ width: "14%", paddingHorizontal: 4 }}>Installment</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Amount</Text>
          <Text style={{ width: "16%", paddingHorizontal: 4 }}>Bank / FDR</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Date</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Maturity</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>Renewal</Text>
          <Text style={{ width: "12%", paddingHorizontal: 4 }}>IFSC</Text>
        </View>
        {rows.map((item, index) => (
          <View key={`install-${index}`} style={pdfStyles.tableRow}>
            <Text style={{ width: "10%", paddingHorizontal: 4 }}>{index + 1}</Text>
            <Text style={{ width: "14%", paddingHorizontal: 4 }}>{formatPlain(item.installment_no)}</Text>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatPlain(item.amount)}</Text>
            <Text style={{ width: "16%", paddingHorizontal: 4 }}>{formatPlain(item.fdr_bank_number || item.fdr_no)}</Text>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatDate(item.corpus_date || item.date)}</Text>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatDate(item.date_of_maturity || item.date_maturity)}</Text>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatDate(item.date_of_renewal || item.date_renewal)}</Text>
            <Text style={{ width: "12%", paddingHorizontal: 4 }}>{formatPlain(item.ifsc || item.ifsc_code)}</Text>
          </View>
        ))}
      </View>
    </React.Fragment>
  );
};

const DeficienciesTable = ({ finalStatus, rows = [] }) => {
  const validRows = (rows || []).filter((item) => item?.deficiences || item?.deficiency || item?.remarks);
  if (finalStatus !== "DeficiencyIdentified" || !validRows.length) {
    return null;
  }

  return (
    <React.Fragment key="Deficiencies Found">
      <Text style={pdfStyles.sectionTitle}>10. Deficiencies Found</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={{ width: "10%", paddingHorizontal: 4 }}>Sl.</Text>
          <Text style={{ width: "90%", paddingHorizontal: 4 }}>Deficiency Identified</Text>
        </View>
        {validRows.map((item, index) => (
          <View key={`def-${index}`} style={pdfStyles.tableRow}>
            <Text style={{ width: "10%", paddingHorizontal: 4 }}>{index + 1}</Text>
            <Text style={{ width: "90%", paddingHorizontal: 4 }}>{formatPlain(item.deficiences || item.deficiency || item.remarks)}</Text>
          </View>
        ))}
      </View>
    </React.Fragment>
  );
};

const FinalRemarksStatusTable = ({ values = {} }) => {
  const finalRemarks = getPdfField(values, "finalRemarks", "final_remarks", "remarks");
  const finalStatus = getPdfField(values, "finalStatus", "final_status", "status");

  return (
    <React.Fragment key="Final Remarks and Status">
      <Text style={pdfStyles.sectionTitle}>9. Final Remarks and Status</Text>
      <View style={pdfStyles.table}>
        <View style={pdfStyles.tableHeader}>
          <Text style={{ width: "50%", paddingHorizontal: 4 }}>Final Remarks</Text>
          <Text style={{ width: "50%", paddingHorizontal: 4 }}>Final Status</Text>
        </View>
        <View style={pdfStyles.tableRow}>
          <Text style={{ width: "50%", paddingHorizontal: 4 }}>{formatPlain(finalRemarks)}</Text>
          <Text style={{ width: "50%", paddingHorizontal: 4 }}>{formatPlain(finalStatus)}</Text>
        </View>
      </View>
    </React.Fragment>
  );
};

const buildSocietyRows = (values = {}) => [
  {
    sno: "1.1",
    label: "Whether the Registration certificate of the Society is uploaded",
    existing: getPdfField(values, "regCertHandingover", "reg_cert_handingover", "reg_cert_handing"),
    proposed: getPdfField(values, "regCertTakingover", "reg_cert_takingover", "reg_cert_taking"),
    remarks: getPdfField(values, "regCertRemarks", "reg_cert_remarks"),
    yesNo: true,
  },
  {
    sno: "1.2",
    label: "Whether a copy of the Bye-laws of the Society is uploaded",
    existing: getPdfField(values, "byeLawsHandingover", "bye_laws_handingover", "bye_laws_handing"),
    proposed: getPdfField(values, "byeLawsTakingover", "bye_laws_takingover", "bye_laws_taking"),
    remarks: getPdfField(values, "byeLawsRemarks", "bye_laws_remarks"),
    yesNo: true,
  },
  {
    sno: "1.3",
    label: "Whether a copy of the Resolutions of the Society for Change of Management is uploaded",
    existing: getPdfField(values, "resolutionsHandingover", "resolutions_handingover", "resolutions_handing"),
    proposed: getPdfField(values, "resolutionsTakingover", "resolutions_takingover", "resolutions_taking"),
    remarks: getPdfField(values, "resolutionsRemarks", "resolutions_remarks"),
    yesNo: true,
  },
  {
    sno: "1.4",
    label: "Whether the resolution(s) made is valid as per bye-laws",
    existing: getPdfField(values, "resolutionValidHandingover", "resolution_valid_handingover", "resolutionvalidhandingover"),
    proposed: getPdfField(values, "resolutionValidTakingover", "resolution_valid_takingover", "resolutionvalidtakingover"),
    remarks: getPdfField(values, "resolutionValidRemarks", "resolution_valid_remarks", "resolutionvalidremarks"),
  },
  {
    sno: "1.5",
    label: "If any member(s) of the society is/are common member(s) of both the societies, provide details in remarks",
    existing: getPdfField(values, "commonMembersExists", "common_members_exists", "commonmembersexists"),
    proposed: "-",
    remarks: getPdfField(values, "commonMembersRemarks", "common_members_remarks", "commonmembersremarks"),
  },
  {
    sno: "1.6",
    label: "If the change of management also involves the transfer of fixed assets i.e, land and building, mention the details",
    existing: getPdfField(values, "fixedAssetsTransfer", "fixed_assets_transfer", "fixedassetstransfer"),
    proposed: "-",
    remarks: getPdfField(values, "fixedAssetsRemarks", "fixed_assets_remarks", "fixedassetsremarks"),
  },
];

const buildCollegeRows = (values = {}) => [
  { sno: "2.1", label: "Name of the College", value: getPdfField(values, "collegeName"), remarks: getPdfField(values, "collegeNameRemarks") },
  { sno: "2.2", label: "Address of the College", value: `${getPdfField(values, "doorNo")} ${getPdfField(values, "villageTown")}`.trim(), remarks: getPdfField(values, "collegeAddressRemarks") },
  {
    sno: "2.3",
    label: "Name and Contact Details of the Principal of the College (email, Mobile etc.)",
    value: getPdfField(values, "principalContactDetails"),
    remarks: getPdfField(values, "principalContactRemarks"),
  },
  { sno: "2.4", label: "Jnanabhumi Code of the College", value: getPdfField(values, "jnanabhumiCode"), remarks: getPdfField(values, "jnanabhumiCodeRemarks") },
  { sno: "2.5", label: "AISHE Code", value: getPdfField(values, "aisheCode"), remarks: getPdfField(values, "aisheCodeRemarks") },
  { sno: "2.6", label: "Year of Establishment", value: getPdfField(values, "yearOfEstablishment"), remarks: getPdfField(values, "yearOfEstablishmentRemarks") },
  { sno: "2.7", label: "Name of the Mandal where the existing college is located", value: getPdfField(values, "mandalName"), remarks: getPdfField(values, "mandalNameRemarks") },
  { sno: "2.8", label: "Name of the District where the existing college is located", value: getPdfField(values, "districtName"), remarks: getPdfField(values, "districtNameRemarks") },
  { sno: "2.9", label: "Status of Mandal", value: getPdfField(values, "mandalStatus"), remarks: getPdfField(values, "mandalStatusRemarks") },
  {
    sno: "2.10",
    label: "Whether a copy of the permission order granted for the establishment of the college is uploaded.",
    value: getPdfField(values, "permissionOrderUploaded"),
    remarks: getPdfField(values, "permissionOrderRemarks"),
    yesNo: true,
  },
  {
    sno: "2.11",
    label: "University affiliation order (2025-26) is uploaded",
    value: getPdfField(values, "universityAffiliationUploaded"),
    remarks: getPdfField(values, "universityAffiliationRemarks"),
    yesNo: true,
  },
  { sno: "2.12", label: "Whether a copy of the last EoP granted is uploaded", value: getPdfField(values, "lastEopUploaded"), remarks: getPdfField(values, "lastEopRemarks"), yesNo: true },
  {
    sno: "2.13",
    label: "Whether proof of the registration and processing fee paid is uploaded",
    value: getPdfField(values, "feeProofUploaded"),
    remarks: getPdfField(values, "feeProofRemarks"),
    yesNo: true,
  },
];

const buildLandRows = (values = {}, landDetails = {}) => [
  {
    sno: "3.1",
    label: "Whether the society has own land as per norms",
    existing: getPdfField(values, "ownLandHandingover"),
    proposed: getPdfField(values, "ownLandTakingover"),
    remarks: getPdfField(values, "ownLandRemarks"),
    yesNo: true,
  },
  {
    sno: "3.2",
    label: "Whether the registered ownership of the land deed is produced",
    existing: getPdfField(values, "landDeedProducedHandingover"),
    proposed: getPdfField(values, "landDeedProducedTakingover"),
    remarks: getPdfField(values, "landDeedProducedRemarks"),
    yesNo: true,
    docUrl: landDetails?.lease_deed ? buildDocUrl(landDetails.lease_deed) : "",
  },
  {
    sno: "3.3",
    label: "Whether the registered document of the land owned in the name of the Society",
    existing: getPdfField(values, "landInSocietyNameHandingover", "land_in_society_name_handingover"),
    proposed: getPdfField(values, "landInSocietyNameTakingover", "land_in_society_name_takingover"),
    remarks: getPdfField(values, "landInSocietyNameRemarks", "land_in_society_name_remarks"),
  },
  {
    sno: "3.4",
    label: "Whether the own land is provided in the same Mandal",
    existing: getPdfField(values, "landInSameMandalHandingover", "land_in_same_mandal_handingover"),
    proposed: getPdfField(values, "landInSameMandalTakingover", "land_in_same_mandal_takingover"),
    remarks: getPdfField(values, "landInSameMandalRemarks", "land_in_same_mandal_remarks"),
  },
  {
    sno: "3.5",
    label: "Whether the sketch plan of own land drawn by the licensed surveyor and attested by the MRO is produced",
    existing: getPdfField(values, "sketchPlanProducedHandingover", "sketch_plan_produced_handingover"),
    proposed: getPdfField(values, "sketchPlanProducedTakingover", "sketch_plan_produced_takingover"),
    remarks: getPdfField(values, "sketchPlanProducedRemarks", "sketch_plan_produced_remarks"),
    docUrl: landDetails?.khasra_plan_file ? buildDocUrl(landDetails.khasra_plan_file) : "",
  },
  {
    sno: "3.6",
    label: "Whether the schedule of property mentioned in the registered ownership deed is tallying with the sketch plan produced",
    existing: getPdfField(values, "propertyScheduleTallyHandingover", "property_schedule_tally_handingover"),
    proposed: getPdfField(values, "propertyScheduleTallyTakingover", "property_schedule_tally_takingover"),
    remarks: getPdfField(values, "propertyScheduleTallyRemarks", "property_schedule_tally_remarks"),
  },
];

const getDocPath = (docs = [], docId) => {
  const doc = docs.find((d) => d.doc_id === docId && d.doc_path);
  return doc ? buildDocUrl(doc.doc_path) : "";
};

const buildBuildingRows = (values = {}, docs = []) => [
  {
    sno: "4.1",
    label: "Address of the proposed building",
    existing:
      `${getPdfField(values, "buildingAddressSurveyExisting", "building_address_survey_existing", "survey_no")} ${getPdfField(values, "buildingAddressTownExisting", "building_address_town_existing", "village_name")}`.trim(),
    proposed: getPdfField(values, "buildingAddressProposed", "building_address_proposed"),
    remarks: getPdfField(values, "buildingAddressRemarks", "building_address_remarks"),
  },
  {
    sno: "4.2",
    label: "Same Mandal",
    existing: getPdfField(values, "sameMandalExisting", "same_mandal_existing"),
    proposed: getPdfField(values, "sameMandalProposed", "same_mandal_proposed"),
    remarks: getPdfField(values, "sameMandalRemarks", "same_mandal_remarks"),
  },
  {
    sno: "4.3",
    label: "Permanent RCC Building",
    existing: getPdfField(values, "permanentBuildingExisting", "permanent_building_existing"),
    proposed: getPdfField(values, "permanentBuildingProposed", "permanent_building_proposed"),
    remarks: getPdfField(values, "permanentBuildingRemarks", "permanent_building_remarks"),
  },
  {
    sno: "4.4",
    label: "Distance Requirement",
    existing: getPdfField(values, "distanceRequirementExisting", "distance_requirement_existing"),
    proposed: getPdfField(values, "distanceRequirementProposed", "distance_requirement_proposed"),
    remarks: getPdfField(values, "distanceRequirementRemarks", "distance_requirement_remarks"),
  },
  {
    sno: "4.5",
    label: "Whether the building is owned / leased",
    existing: getPdfField(values, "buildingOwnershipExisting", "building_ownership_existing"),
    proposed: getPdfField(values, "buildingOwnershipProposed", "building_ownership_proposed"),
    remarks: getPdfField(values, "buildingOwnershipRemarks", "building_ownership_remarks"),
  },
  {
    sno: "4.6",
    label: "If leased, whether the registered lease deed of the building is enclosed",
    existing: getPdfField(values, "leasedDocumentExisting", "leased_document_existing"),
    proposed: getPdfField(values, "leasedDocumentProposed", "leased_document_proposed"),
    remarks: getPdfField(values, "leasedDocumentRemarks", "leased_document_remarks"),
  },
  {
    sno: "a",
    label: "Whether the lease document is registered in the name of the Society",
    existing: getPdfField(values, "leaseRegisteredInSocietyExisting", "lease_registered_in_society_existing"),
    proposed: getPdfField(values, "leaseRegisteredInSocietyProposed", "lease_registered_in_society_proposed", "society"),
    remarks: getPdfField(values, "leaseRegisteredInSocietyRemarks", "lease_registered_in_society_remarks"),
  },
  {
    sno: "b",
    label: "Year of registration of lease document",
    existing: getPdfField(values, "leaseRegistrationYearExisting", "lease_registration_year_existing"),
    proposed: getPdfField(values, "leaseRegistrationYearProposed", "lease_registration_year_proposed"),
    remarks: getPdfField(values, "leaseRegistrationYearRemarks", "lease_registration_year_remarks"),
  },
  {
    sno: "c",
    label: "Validity of the lease period",
    existing: `${formatDate(getPdfField(values, "leaseValidityFromExisting", "lease_validity_from_existing"))} - ${formatDate(getPdfField(values, "leaseValidityToExisting", "lease_validity_to_existing"))}`,
    proposed: `${formatPlain(getPdfField(values, "leaseValidityFromProposed", "lease_validity_from_proposed"))} - ${formatPlain(getPdfField(values, "leaseValidityToProposed", "lease_validity_to_proposed"))}`,
    remarks: getPdfField(values, "leaseValidityRemarks", "lease_validity_remarks"),
  },
  {
    sno: "d",
    label: "Total no. of years completed in leased building",
    existing: getPdfField(values, "yearsCompletedExisting", "years_completed_existing"),
    proposed: getPdfField(values, "yearsCompletedProposed", "years_completed_proposed", "no_years"),
    remarks: getPdfField(values, "yearsCompletedRemarks", "years_completed_remarks"),
  },
  {
    sno: "e",
    label: "Remaining no. of years of the lease period as on date of application",
    existing: getPdfField(values, "remainingLeaseYearsExisting", "remaining_lease_years_existing"),
    proposed: getPdfField(values, "remainingLeaseYearsProposed", "remaining_lease_years_proposed"),
    remarks: getPdfField(values, "remainingLeaseRemarks", "remaining_lease_remarks"),
  },
  {
    sno: "4.7",
    label: "If own, whether the registered sale deed of the building is uploaded ",
    existing: getPdfField(values, "saleDeedUploadedExisting", "sale_deed_uploaded_existing"),
    proposed: getPdfField(values, "saleDeedUploadedProposed", "sale_deed_uploaded_proposed"),
    remarks: getPdfField(values, "saleDeedUploadedRemarks", "sale_deed_uploaded_remarks"),
    yesNo: true,
  },
  {
    sno: "4.8",
    label: "Whether the sale deed is registered in the name of the Society",
    existing: getPdfField(values, "saleDeedInSocietyExisting", "sale_deed_in_society_existing"),
    proposed: getPdfField(values, "saleDeedInSocietyProposed", "sale_deed_in_society_proposed"),
    remarks: getPdfField(values, "saleDeedInSocietyRemarks", "sale_deed_in_society_remarks"),
    yesNo: true,
  },
  {
    sno: "4.9",
    label: "Whether the plan of the building accommodation is approved by competent authority for running the institution",
    existing: getPdfField(values, "planApprovedExisting", "plan_approved_existing"),
    proposed: getPdfField(values, "planApprovedProposed", "plan_approved_proposed"),
    remarks: getPdfField(values, "planApprovedRemarks", "plan_approved_remarks"),
    yesNo: true,
    docUrl: getDocPath(docs, 4),
  },
  {
    sno: "4.10",
    label: "Whether approved plan of the building drawn by licensed surveyor and attested by the Principal of a Government College is uploaded",
    existing: getPdfField(values, "approvedPlanUploadedExisting", "approved_plan_uploaded_existing"),
    proposed: getPdfField(values, "approvedPlanUploadedProposed", "approved_plan_uploaded_proposed"),
    remarks: getPdfField(values, "approvedPlanUploadedRemarks", "approved_plan_uploaded_remarks"),
    yesNo: true,
    docUrl: getDocPath(docs, 11),
  },
  {
    sno: "4.11",
    label: "Carpet area of the Building (in Sft)",
    existing: getPdfField(values, "carpetAreaExisting", "carpet_area_existing"),
    proposed: getPdfField(values, "carpetAreaProposed", "carpet_area_proposed"),
    remarks: getPdfField(values, "carpetAreaRemarks", "carpet_area_remarks"),
  },
  {
    sno: "4.12",
    label: "Whether a copy of the construction permission order for the leased / own building issued by competent authority is uploaded",
    existing: getPdfField(values, "constructionPermissionExisting", "construction_permission_existing"),
    proposed: getPdfField(values, "constructionPermissionProposed", "construction_permission_proposed"),
    remarks: getPdfField(values, "constructionPermissionRemarks", "construction_permission_remarks"),
    yesNo: true,
    docUrl: getDocPath(docs, 12),
  },
  {
    sno: "4.13",
    label: "Whether valid Sanitary Certificate is uploaded",
    existing: getPdfField(values, "sanitaryCertificateExisting", "sanitary_certificate_existing"),
    proposed: getPdfField(values, "sanitaryCertificateProposed", "sanitary_certificate_proposed"),
    remarks: getPdfField(values, "sanitaryCertificateRemarks", "sanitary_certificate_remarks"),
    yesNo: true,
    docUrl: getDocPath(docs, 15),
  },
  {
    sno: "4.14",
    label: "Whether latest Geotagged photographs of the building attested by the Principal of a Government College are uploaded",
    existing: getPdfField(values, "geoTaggedPhotosExisting", "geo_tagged_photos_existing"),
    proposed: getPdfField(values, "geoTaggedPhotosProposed", "geo_tagged_photos_proposed"),
    remarks: getPdfField(values, "geoTaggedPhotosRemarks", "geo_tagged_photos_remarks"),
    yesNo: true,
    docUrl: getDocPath(docs, 16),
  },
  {
    sno: "4.15",
    label: "Whether any other Institution is running in the same building accommodation",
    existing: getPdfField(values, "otherInstitutionExisting", "other_institution_existing"),
    proposed: getPdfField(values, "otherInstitutionProposed", "other_institution_proposed") || (values?.same_primises === true ? "Yes" : values?.same_primises === false ? "No" : ""),
    remarks: getPdfField(values, "otherInstitutionRemarks", "other_institution_remarks"),
    yesNo: true,
  },
  {
    sno: "4.16",
    label: "If yes, whether NoC from the concerned authority to utilize part of the building, which is not being utilized by the other institution, for running this institution is uploaded",
    existing: getPdfField(values, "nocUploadedExisting", "noc_uploaded_existing"),
    proposed: getPdfField(values, "nocUploadedProposed", "noc_uploaded_proposed"),
    remarks: getPdfField(values, "nocUploadedRemarks", "noc_uploaded_remarks"),
    yesNo: true,
  },
  {
    sno: "4.17",
    label: "Whether the details of existing building accommodation provided in Annexure-I are in accordance with the stipulated norms",
    existing: getPdfField(values, "annexureDetailsExisting", "annexure_details_existing"),
    proposed: getPdfField(values, "annexureDetailsProposed", "annexure_details_proposed"),
    remarks: getPdfField(values, "annexureDetailsRemarks", "annexure_details_remarks"),
    yesNo: true,
    docUrl: getDocPath(docs, 26),
  },
];

const buildStaffRows = (values = {}) => {
  const staffAdequate = getPdfField(values, "staffAdequate", "staff_adequate");
  const rows = [
    {
      sno: "a",
      label: "Whether adequate teaching and supporting staff are provided to run the colleges as per the University / UGC norms",
      value: staffAdequate,
      remarks: getPdfField(values, "staffAdequateRemarks", "staff_adequate_remarks"),
      yesNo: true,
    },
  ];

  if (String(staffAdequate).trim() === "No") {
    rows.push({
      sno: "b",
      label: "If no, mention the deficiencies",
      value: getPdfField(values, "staffDeficiencies", "staff_deficiencies"),
      remarks: getPdfField(values, "staffDeficienciesRemarks", "staff_deficiencies_remarks"),
    });
  }

  rows.push({
    sno: "c",
    label: "Whether undertaking on Rs. 100 non-judicial bond paper is submitted by both the societies",
    value: getPdfField(values, "undertakingBondSubmitted", "undertaking_bond_submitted"),
    remarks: getPdfField(values, "undertakingBondRemarks", "undertaking_bond_remarks"),
  });

  return rows;
};

const buildParkingRows = (values = {}) => {
  const parkingAreaSamePremises = getPdfField(values, "parkingAreaSamePremisesExisting", "parking_area_same_premises_existing");
  const parkingAreaWithin100m = getPdfField(values, "parkingAreaWithin100mExisting", "parking_area_within_100m_existing");

  // Always include row 'a'
  const rows = [
    {
      sno: "a",
      label: "Whether the parking area is available in the same premises",
      value: parkingAreaSamePremises,
      remarks: getPdfField(values, "parkingAreaSamePremisesRemarks", "parking_area_same_premises_remarks"),
      yesNo: true,
    },
  ];

  // If 'a' is Yes, show 'b'
  const isSamePremisesYes = String(parkingAreaSamePremises).trim().toLowerCase() === "yes";
  if (isSamePremisesYes) {
    rows.push({
      sno: "b",
      label: "Whether 20% of the total built up area is provided for the Parking area",
      value: getPdfField(values, "parkingArea20PercentExisting", "parking_area_20_percent_existing"),
      remarks: getPdfField(values, "parkingArea20PercentRemarks", "parking_area_20_percent_remarks"),
      yesNo: true,
    });
  }

  // If 'a' is No, show 'c'
  const isSamePremisesNo = String(parkingAreaSamePremises).trim().toLowerCase() === "no";
  if (isSamePremisesNo) {
    rows.push({
      sno: "c",
      label: "If not in the same premises, whether the parking area provided within a distance of 100 meters thereof",
      value: parkingAreaWithin100m,
      remarks: getPdfField(values, "parkingAreaWithin100mRemarks", "parking_area_within_100m_remarks"),
      yesNo: true,
    });

    // If 'c' is Yes, show 'd'
    const isWithin100mYes = String(parkingAreaWithin100m).trim().toLowerCase() === "yes";
    if (isWithin100mYes) {
      rows.push({
        sno: "d",
        label: "Whether the document is uploaded for proof of parking area within 100 meters",
        value: getPdfField(values, "parkingAreaWithin100mDocUploaded", "parking_area_within_100m_doc_uploaded"),
        remarks: getPdfField(values, "parkingAreaWithin100mDocRemarks", "parking_area_within_100m_doc_remarks"),
        yesNo: true,
      });
    }
  }

  return rows;
};

const buildPlaygroundRows = (values = {}) => [
  {
    sno: "a",
    label: "Whether the Playground facility is available in the same premises",
    value: getPdfField(values, "playgroundSamePremisesExisting", "playground_same_premises_existing"),
    remarks: getPdfField(values, "playgroundSamePremisesRemarks", "playground_same_premises_remarks"),
    yesNo: true,
  },
  {
    sno: "b",
    label: "If yes, mention the extent of the playground facility available (sft)",
    value: getPdfField(values, "playgroundExtentExisting", "playground_extent_existing"),
    remarks: getPdfField(values, "playgroundExtentRemarks", "playground_extent_remarks"),
  },
  {
    sno: "c",
    label: "If not, whether any alternative arrangement for playground is provided within a radius of 5 kms along with bus facility",
    value: getPdfField(values, "playgroundAlternativeExisting", "playground_alternative_existing"),
    remarks: getPdfField(values, "playgroundAlternativeRemarks", "playground_alternative_remarks"),
    yesNo: true,
  },
  {
    sno: "d",
    label: "If yes, whether MoU for utilization of the alternative facility for playground is produced",
    value: getPdfField(values, "playgroundMouExisting", "playground_mou_existing"),
    remarks: getPdfField(values, "playgroundMouRemarks", "playground_mou_remarks"),
    yesNo: true,
  },
];

const buildFireSafetyRows = (values = {}) => [
  {
    sno: "4.21",
    label: "Whether valid fire safety certificate / latest affidavit for exempted cases is produced",
    value: getPdfField(values, "fireSafetyCertificateExisting", "fire_safety_certificate_existing"),
    remarks: getPdfField(values, "fireSafetyCertificateRemarks", "fire_safety_certificate_remarks"),
    yesNo: true,
  },
];

export const ChangeOfManagementPdfDocument = ({ pdfData = {} }) => {
  const {
    collCode,
    society = {},
    formValues = {},
    insertBy = "",
    userName = "",
    ugPrograms = [],
    pgPrograms = [],
    accommodationRows = [],
    accommodationBaseRows = [],
    corpusFundInstall = [],
    ugCorpusFunds = [],
    pgBefore = [],
    pgCorpusFunds = [],
    deficiencies = [],
    managementDocs = [],
    nocdata = {},
  } = pdfData;

  const pdfLandDetails = Array.isArray(nocdata?.land) ? nocdata.land[0] || {} : {};

  console.log("PDF Data:", formValues, pdfLandDetails);
  const handingOverSociety = [
    { label: "Society Name", value: getValue(society, "current_name_of_trust") },
    { label: "Village / Town", value: getValue(society, "village_name") },
    { label: "Mandal", value: getValue(society, "mandal_name") },
    { label: "District", value: getValue(society, "dist_name") },
  ];

  const takingOverSociety = [
    { label: "Society Name", value: getValue(society, "proposed_new_name") },
    { label: "Village / Town", value: getValue(society, "village_name") },
    { label: "Mandal", value: getValue(society, "mandal_name") },
    { label: "District", value: getValue(society, "dist_name") },
  ];

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.header}>
          <Text style={pdfStyles.headerTitle}>{formatPlain(society.proposed_new_name || "College")}</Text>
          <Text style={pdfStyles.headerSubtitle}>Scrutiny Report for Change of Management of Private Unaided Degree College</Text>
          <Text style={pdfStyles.headerDate}>Generated on {new Date().toLocaleDateString()}</Text>
        </View>

        {renderSummaryTable("Handing Over Society and Address", handingOverSociety)}
        {renderSummaryTable("Taking Over Society and Address", takingOverSociety)}
        <SectionComparisonTable
          title="1. Basic Details of the Societies"
          rows={buildSocietyRows(formValues)}
          particularsLabel="Societies Details"
          existingLabel="Handing-over Society"
          proposedLabel="Taking-over Society"
        />
        <SimpleChecklistTable
          title="2. Basic Details of the College"
          rows={buildCollegeRows(formValues)}
          particularsLabel="Details"
          valueLabel="Please tick"
          remarksLabel="Remarks of the Scrutiny Committee"
        />
        <SectionComparisonTable
          title="3. Details of the Own Land"
          rows={buildLandRows(formValues, pdfLandDetails)}
          particularsLabel="Land Details"
          existingLabel="Handing-over Society"
          proposedLabel="Taking-over Society (If same as Handing-over Society: auto populate)"
        />
        <SectionComparisonTable
          title="4. Details of the Building"
          rows={buildBuildingRows(formValues, managementDocs)}
          particularsLabel="Building Details"
          existingLabel="Existing"
          proposedLabel="Proposed (If same as existing: auto populate)"
        />
        <AccommodationTable rows={accommodationRows} baseRows={accommodationBaseRows} />
        <SimpleChecklistTable title="4.19 Parking area" rows={buildParkingRows(formValues)} particularsLabel="Description" valueLabel="Please tick" remarksLabel="Remarks of the Scrutiny Committee" />
        <SimpleChecklistTable title="4.20 Playground" rows={buildPlaygroundRows(formValues)} particularsLabel="Description" valueLabel="Please tick" remarksLabel="Remarks of the Scrutiny Committee" />
        <SimpleChecklistTable
          title="4.21 Fire Safety"
          rows={buildFireSafetyRows(formValues)}
          particularsLabel="Description"
          valueLabel="Please tick"
          remarksLabel="Remarks of the Scrutiny Committee"
        />
        <ProgrammesTable title="5. Details of the UG programmes" rows={ugPrograms} typeLabel="Type of the UG Programme" />
        <ProgrammesTable title="5A. Details of the PG programmes" rows={pgPrograms} typeLabel="Type of the PG Programme" />
        <CorpusFundsTable title="For Degree Colleges / programmes sanctioned after 2021-22" rows={ugCorpusFunds} />
        <CorpusFundsTable title="For existing PG Programmes sanctioned before 2021-22" rows={pgBefore} />
        <CorpusFundsTable title="For existing PG Programmes sanctioned after 2021-22" rows={pgCorpusFunds} />
        <CorpusFundChecklistTable values={formValues} />
        <InstallmentTable rows={corpusFundInstall} title="For Degree Colleges established before 2021-22" />
        <SimpleChecklistTable title="8. Staff particulars" rows={buildStaffRows(formValues)} particularsLabel="Description" valueLabel="Please tick" remarksLabel="Remarks of the Scrutiny Committee" />
        <FinalRemarksStatusTable values={formValues} />
        <DeficienciesTable finalStatus={getPdfField(formValues, "finalStatus", "final_status", "status")} rows={deficiencies} />

   {/* {(insertBy || userName) && (
  <View style={{ marginTop: 15, marginBottom: 10 }}>
    <Text style={{ fontSize: 9, fontWeight: 'bold' }}>
      {userName && `Inspected by  ${formatPlain(userName)}`}
      {insertBy && ` (${formatPlain(insertBy)})`}
    </Text>
  </View>
)} */}
        <View style={pdfStyles.footer}>
          <Text>College Code: {formatPlain(collCode)} | This is a system-generated Change of Management scrutiny report.</Text>
        </View>
      </Page>
    </Document>
  );
};

export const buildChangeOfManagementPdfData = ({
  collCode,
  societyName,
  formValues,
  managementData,
  scrutinyDetails,
  mngmtScrutiny,
  deficiencies,
  accommodationRows,
  accommodationBaseRows,
  corpusFundInstall,
  ugPrograms,
  pgPrograms,
  ugCorpusFunds,
  pgBefore,
  pgCorpusFunds,
  nocdata,
  parking,
}) => {
  const managementPayload = managementData && !Array.isArray(managementData) ? managementData : {};

  let insertBy = "";
  let userName = "";
  if (Array.isArray(managementPayload?.mngmtScrutiny) && managementPayload.mngmtScrutiny.length > 0) {
    insertBy = managementPayload.mngmtScrutiny[0]?.insert_by || "";
    userName = managementPayload.mngmtScrutiny[0]?.user_name || "";
  }
  const scrutinyPayload = scrutinyDetails && !Array.isArray(scrutinyDetails) ? scrutinyDetails : {};
  // console.log("management data>>",managementPayload?.mngmtScrutiny[0]?.insert_by);
  // Get scrutiny from mngmtScrutiny parameter first, then fallback to managementData
  let scrutiny = {};
  if (Array.isArray(mngmtScrutiny) && mngmtScrutiny.length > 0) {
    scrutiny = mngmtScrutiny[0] || {};
  } else if (Array.isArray(managementData)) {
    scrutiny = managementData[0] || {};
  } else if (Array.isArray(managementPayload?.mngmtScrutiny) && managementPayload.mngmtScrutiny.length > 0) {
    scrutiny = managementPayload.mngmtScrutiny[0] || {};
  }

  const instDetails = Array.isArray(nocdata?.instDetails) ? nocdata.instDetails[0] || {} : {};
  const landDetails = Array.isArray(nocdata?.land) ? nocdata.land[0] || {} : {};
  const allDocs = Array.isArray(nocdata?.document) ? nocdata.document : [];
  const managementDocs = allDocs.filter((doc) => doc.noc_type === "management");
  const documentDetails = allDocs[0] || {};
  const hasDoc = (docId) => managementDocs.some((doc) => doc.doc_id === docId && doc.doc_path);
  const scrutinyDetailsData = Array.isArray(scrutinyPayload?.data) ? scrutinyPayload.data[0] || {} : {};
  const scrutinyParking = Array.isArray(scrutinyPayload?.parking) ? scrutinyPayload.parking[0] || {} : {};
  const scrutinyInstallments = Array.isArray(scrutinyPayload?.corpusFundInstall) ? scrutinyPayload.corpusFundInstall : [];
  const scrutinyUgPrograms = Array.isArray(scrutinyPayload?.ugCourses) ? scrutinyPayload.ugCourses : [];
  const scrutinyPgPrograms = Array.isArray(scrutinyPayload?.pgCourses) ? scrutinyPayload.pgCourses : [];
  const scrutinyCorpusFunds = Array.isArray(scrutinyPayload?.corpusfundUGPG) ? scrutinyPayload.corpusfundUGPG : [];
  const scrutinyUgFunds = scrutinyCorpusFunds.filter(
    (item) =>
      String(item?.coll_type || "")
        .trim()
        .toLowerCase() === "ug",
  );
  const scrutinyPgBeforeFunds = scrutinyCorpusFunds.filter(
    (item) =>
      String(item?.coll_type || "")
        .trim()
        .toLowerCase() === "pg before",
  );
  const scrutinyPgAfterFunds = scrutinyCorpusFunds.filter(
    (item) =>
      String(item?.coll_type || "")
        .trim()
        .toLowerCase() === "pg after",
  );

  // MAP ALL scrutiny fields from backend snake_case to camelCase form values
  const scrutinyMappedValues = {
    regCertHandingover: scrutiny.reg_cert_handingover || "",
    regCertTakingover: scrutiny.reg_cert_takingover || "",
    regCertRemarks: scrutiny.reg_cert_remarks || "",
    byeLawsHandingover: scrutiny.bye_laws_handingover || "",
    byeLawsTakingover: scrutiny.bye_laws_takingover || "",
    byeLawsRemarks: scrutiny.bye_laws_remarks || "",
    resolutionsHandingover: scrutiny.resolutions_handingover || "",
    resolutionsTakingover: scrutiny.resolutions_takingover || "",
    resolutionsRemarks: scrutiny.resolutions_remarks || "",
    resolutionValidHandingover: getValue(scrutiny, "resolution_valid_handingover", "resolutionvalidhandingover"),
    resolutionValidTakingover: getValue(scrutiny, "resolution_valid_takingover", "resolutionvalidtakingover"),
    resolutionValidRemarks: getValue(scrutiny, "resolution_valid_remarks", "resolutionvalidremarks"),
    commonMembersExists: getValue(scrutiny, "common_members_exists", "commonmembersexists"),
    commonMembersRemarks: getValue(scrutiny, "common_members_remarks", "commonmembersremarks"),
    fixedAssetsTransfer: getValue(scrutiny, "fixed_assets_transfer", "fixedassetstransfer"),
    fixedAssetsRemarks: getValue(scrutiny, "fixed_assets_remarks", "fixedassetsremarks"),
    collegeNameRemarks: scrutiny.college_name_remarks || "",
    collegeAddressRemarks: scrutiny.college_address_remarks || "",
    principalContactRemarks: scrutiny.principal_contact_remarks || "",
    jnanabhumiCodeRemarks: scrutiny.jnanabhumi_code_remarks || "",
    aisheCodeRemarks: scrutiny.aishe_code_remarks || "",
    yearOfEstablishmentRemarks: scrutiny.year_of_establishment_remarks || "",
    mandalNameRemarks: scrutiny.mandal_name_remarks || "",
    districtNameRemarks: scrutiny.district_name_remarks || "",
    mandalStatus: scrutiny.mandal_status || "",
    mandalStatusRemarks: scrutiny.mandal_status_remarks || "",
    permissionOrderUploaded: scrutiny.permission_order_uploaded || "",
    permissionOrderRemarks: scrutiny.permission_order_remarks || "",
    universityAffiliationUploaded: scrutiny.university_affiliation_uploaded || "",
    universityAffiliationRemarks: scrutiny.university_affiliation_remarks || "",
    lastEopUploaded: scrutiny.last_eop_uploaded || "",
    lastEopRemarks: scrutiny.last_eop_remarks || "",
    feeProofUploaded: scrutiny.fee_proof_uploaded || "",
    feeProofRemarks: scrutiny.fee_proof_remarks || "",
    ownLandHandingover: scrutiny.own_land_handingover || "",
    ownLandTakingover: scrutiny.own_land_takingover || "",
    ownLandRemarks: scrutiny.own_land_remarks || "",
    landDeedProducedHandingover: scrutiny.land_deed_produced_handingover || "",
    landDeedProducedTakingover: scrutiny.land_deed_produced_takingover || "",
    landDeedProducedRemarks: scrutiny.land_deed_produced_remarks || "",
    landInSocietyNameHandingover: scrutiny.land_in_society_name_handingover || "",
    landInSocietyNameTakingover: scrutiny.land_in_society_name_takingover || "",
    landInSocietyNameRemarks: scrutiny.land_in_society_name_remarks || "",
    landInSameMandalHandingover: scrutiny.land_in_same_mandal_handingover || "",
    landInSameMandalTakingover: scrutiny.land_in_same_mandal_takingover || "",
    landInSameMandalRemarks: scrutiny.land_in_same_mandal_remarks || "",
    sketchPlanProducedHandingover: scrutiny.sketch_plan_produced_handingover || "",
    sketchPlanProducedTakingover: scrutiny.sketch_plan_produced_takingover || "",
    sketchPlanProducedRemarks: scrutiny.sketch_plan_produced_remarks || "",
    propertyScheduleTallyHandingover: scrutiny.property_schedule_tally_handingover || "",
    propertyScheduleTallyTakingover: scrutiny.property_schedule_tally_takingover || "",
    propertyScheduleTallyRemarks: scrutiny.property_schedule_tally_remarks || "",
    buildingAddressSurveyExisting: scrutiny.building_address_survey_existing || "",
    buildingAddressDoorExisting: scrutiny.building_address_door_existing || "",
    buildingAddressTownExisting: scrutiny.building_address_town_existing || "",
    buildingAddressProposed: scrutiny.building_address_proposed || "",
    buildingAddressRemarks: scrutiny.building_address_remarks || "",
    sameMandalExisting: scrutiny.same_mandal_existing || "",
    sameMandalProposed: scrutiny.same_mandal_proposed || "",
    sameMandalRemarks: scrutiny.same_mandal_remarks || "",
    permanentBuildingExisting: scrutiny.permanent_building_existing || "",
    permanentBuildingProposed: scrutiny.permanent_building_proposed || "",
    permanentBuildingRemarks: scrutiny.permanent_building_remarks || "",
    distanceRequirementExisting: scrutiny.distance_requirement_existing || "",
    distanceRequirementProposed: scrutiny.distance_requirement_proposed || "",
    distanceRequirementRemarks: scrutiny.distance_requirement_remarks || "",
    buildingOwnershipExisting: scrutiny.building_ownership_existing || "",
    buildingOwnershipProposed: scrutiny.building_ownership_proposed || "",
    buildingOwnershipRemarks: scrutiny.building_ownership_remarks || "",
    leasedDocumentExisting: scrutiny.leased_document_existing || "",
    leasedDocumentProposed: scrutiny.leased_document_proposed || "",
    leasedDocumentRemarks: scrutiny.leased_document_remarks || "",
    leaseRegisteredInSocietyExisting: scrutiny.lease_registered_in_society_existing || "",
    leaseRegisteredInSocietyProposed: scrutiny.lease_registered_in_society_proposed || "",
    leaseRegisteredInSocietyRemarks: scrutiny.lease_registered_in_society_remarks || "",
    leaseRegistrationYearExisting: scrutiny.lease_registration_year_existing || "",
    leaseRegistrationYearProposed: scrutiny.lease_registration_year_proposed || "",
    leaseRegistrationYearRemarks: scrutiny.lease_registration_year_remarks || "",
    leaseValidityFromExisting: scrutiny.lease_validity_from_existing || "",
    leaseValidityToExisting: scrutiny.lease_validity_to_existing || "",
    leaseValidityFromProposed: scrutiny.lease_validity_from_proposed || "",
    leaseValidityToProposed: scrutiny.lease_validity_to_proposed || "",
    leaseValidityRemarks: scrutiny.lease_validity_remarks || "",
    yearsCompletedExisting: scrutiny.years_completed_existing || "",
    yearsCompletedProposed: scrutiny.years_completed_proposed || "",
    yearsCompletedRemarks: scrutiny.years_completed_remarks || "",
    remainingLeaseYearsExisting: scrutiny.remaining_lease_years_existing || "",
    remainingLeaseYearsProposed: scrutiny.remaining_lease_years_proposed || "",
    remainingLeaseRemarks: scrutiny.remaining_lease_remarks || "",
    saleDeedUploadedExisting: scrutiny.sale_deed_uploaded_existing || "",
    saleDeedUploadedProposed: scrutiny.sale_deed_uploaded_proposed || "",
    saleDeedUploadedRemarks: scrutiny.sale_deed_uploaded_remarks || "",
    saleDeedInSocietyExisting: scrutiny.sale_deed_in_society_existing || "",
    saleDeedInSocietyProposed: scrutiny.sale_deed_in_society_proposed || "",
    saleDeedInSocietyRemarks: scrutiny.sale_deed_in_society_remarks || "",
    planApprovedExisting: scrutiny.plan_approved_existing || "",
    planApprovedProposed: scrutiny.plan_approved_proposed || "",
    planApprovedRemarks: scrutiny.plan_approved_remarks || "",
    approvedPlanUploadedExisting: scrutiny.approved_plan_uploaded_existing || "",
    approvedPlanUploadedProposed: scrutiny.approved_plan_uploaded_proposed || "",
    approvedPlanUploadedRemarks: scrutiny.approved_plan_uploaded_remarks || "",
    carpetAreaExisting: scrutiny.carpet_area_existing || "",
    carpetAreaProposed: scrutiny.carpet_area_proposed || "",
    carpetAreaRemarks: scrutiny.carpet_area_remarks || "",
    constructionPermissionExisting: scrutiny.construction_permission_existing || "",
    constructionPermissionProposed: scrutiny.construction_permission_proposed || "",
    constructionPermissionRemarks: scrutiny.construction_permission_remarks || "",
    sanitaryCertificateExisting: scrutiny.sanitary_certificate_existing || "",
    sanitaryCertificateProposed: scrutiny.sanitary_certificate_proposed || "",
    sanitaryCertificateRemarks: scrutiny.sanitary_certificate_remarks || "",
    geoTaggedPhotosExisting: scrutiny.geo_tagged_photos_existing || "",
    geoTaggedPhotosProposed: scrutiny.geo_tagged_photos_proposed || "",
    geoTaggedPhotosRemarks: scrutiny.geo_tagged_photos_remarks || "",
    otherInstitutionExisting: scrutiny.other_institution_existing || "",
    otherInstitutionProposed: scrutiny.other_institution_proposed || "",
    otherInstitutionRemarks: scrutiny.other_institution_remarks || "",
    nocUploadedExisting: scrutiny.noc_uploaded_existing || "",
    nocUploadedProposed: scrutiny.noc_uploaded_proposed || "",
    nocUploadedRemarks: scrutiny.noc_uploaded_remarks || "",
    annexureDetailsExisting: scrutiny.annexure_details_existing || "",
    annexureDetailsProposed: scrutiny.annexure_details_proposed || "",
    annexureDetailsRemarks: scrutiny.annexure_details_remarks || "",
    corpusFund: scrutiny.corpus_fund || "",
    certificateIssued: scrutiny.certificate_issued || "",
    staffAdequate: scrutiny.staff_adequate || "",
    staffAdequateRemarks: scrutiny.staff_adequate_remarks || "",
    staffDeficiencies: scrutiny.staff_deficiencies || "",
    staffDeficienciesRemarks: scrutiny.staff_deficiencies_remarks || "",
    undertakingBondSubmitted: scrutiny.undertaking_bond_submitted || "",
    undertakingBondRemarks: scrutiny.undertaking_bond_remarks || "",
    finalRemarks: scrutiny.final_remarks || "",
    finalStatus: scrutiny.final_status || "",
    parkingAreaSamePremisesExisting: scrutiny.parking_area_same_premises_existing || "",
    parkingAreaSamePremisesRemarks: scrutiny.parking_area_same_premises_remarks || "",
    parkingArea20PercentExisting: scrutiny.parking_area_20_percent_existing || "",
    parkingArea20PercentRemarks: scrutiny.parking_area_20_percent_remarks || "",
    parkingAreaWithin100mExisting: scrutiny.parking_area_within_100m_existing || "",
    parkingAreaWithin100mRemarks: scrutiny.parking_area_within_100m_remarks || "",
    parkingAreaWithin100mDocUploaded: scrutiny.parking_area_doc_uploaded || scrutiny.parking_area_within_100m_doc_uploaded || "",
    parkingAreaWithin100mDocRemarks: scrutiny.parking_area_within_100m_doc_remarks || "",
    playgroundSamePremisesExisting: scrutiny.playground_same_premises_existing || "",
    playgroundSamePremisesRemarks: scrutiny.playground_same_premises_remarks || "",
    playgroundExtentExisting: scrutiny.playground_extent_existing || "",
    playgroundExtentRemarks: scrutiny.playground_extent_remarks || "",
    playgroundAlternativeExisting: scrutiny.playground_alternative_existing || "",
    playgroundAlternativeRemarks: scrutiny.playground_alternative_remarks || "",
    playgroundMouExisting: scrutiny.playground_mou_existing || "",
    playgroundMouRemarks: scrutiny.playground_mou_remarks || "",
    fireSafetyCertificateExisting: scrutiny.fire_safety_certificate_existing || "",
    fireSafetyCertificateRemarks: scrutiny.fire_safety_certificate_remarks || "",
  };

  // Consolidate with other sources
  const consolidatedFormValues = {
    ...scrutinyDetailsData,
    ...formValues,
    ...scrutinyMappedValues,
    // College details from instDetails
    collegeName: formValues?.collegeName || instDetails?.inst_name || "",
    collCode: formValues?.collCode || instDetails?.coll_code || "",
    jnanabhumiCode: formValues?.jnanabhumiCode || instDetails?.coll_code || "",
    aisheCode: formValues?.aisheCode || instDetails?.aishe_code || "",
    yearOfEstablishment: formValues?.yearOfEstablishment || instDetails?.year_of_establishment || "",
    mandalName: formValues?.mandalName || instDetails?.mandal_name || "",
    districtName: formValues?.districtName || instDetails?.dist_name || "",
    doorNo: formValues?.doorNo || instDetails?.inst_address || "",
    villageTown: formValues?.villageTown || instDetails?.village_name || instDetails?.place_town || "",
    principalContactDetails: formValues?.principalContactDetails || `${instDetails.principle_name || ""} ${instDetails.principle_mobile || ""} ${instDetails.principle_email || ""}` || "",

    // Building/Land prefilled values from landDetails (matches ChangeOfManagement getPreviewDetails)
    buildingAddressSurveyExisting: scrutinyMappedValues.buildingAddressSurveyExisting || formValues?.buildingAddressSurveyExisting || landDetails?.survey_no || "",
    buildingAddressTownExisting: scrutinyMappedValues.buildingAddressTownExisting || formValues?.buildingAddressTownExisting || landDetails?.village_name || "",
    leaseRegisteredInSocietyProposed: scrutinyMappedValues.leaseRegisteredInSocietyProposed || formValues?.leaseRegisteredInSocietyProposed || landDetails?.society || "",
    yearsCompletedProposed: scrutinyMappedValues.yearsCompletedProposed || formValues?.yearsCompletedProposed || landDetails?.no_years || "",
    otherInstitutionProposed:
      scrutinyMappedValues.otherInstitutionProposed || formValues?.otherInstitutionProposed || (landDetails?.same_primises === true ? "Yes" : landDetails?.same_primises === false ? "No" : ""),
    buildingOwnershipProposed:
      scrutinyMappedValues.buildingOwnershipProposed || formValues?.buildingOwnershipProposed || (landDetails?.build_own_lease ? "Own" : landDetails?.build_own_lease === false ? "Leased" : ""),

    // Conditional proposed values from landDetails when existing=Yes
    sameMandalProposed: scrutinyMappedValues.sameMandalProposed || formValues?.sameMandalProposed || landDetails?.mandal_name || "",
    distanceRequirementProposed: scrutinyMappedValues.distanceRequirementProposed || formValues?.distanceRequirementProposed || landDetails?.distance || "",

    // Document upload status from documentDetails
    permissionOrderUploaded: scrutinyMappedValues.permissionOrderUploaded || formValues?.permissionOrderUploaded || (documentDetails?.permission_order ? "Yes" : "") || "",
    universityAffiliationUploaded: scrutinyMappedValues.universityAffiliationUploaded || formValues?.universityAffiliationUploaded || (documentDetails?.university_affiliation ? "Yes" : "") || "",
    lastEopUploaded: scrutinyMappedValues.lastEopUploaded || formValues?.lastEopUploaded || (documentDetails?.last_eop ? "Yes" : "") || "",
    feeProofUploaded: scrutinyMappedValues.feeProofUploaded || formValues?.feeProofUploaded || (documentDetails?.fee_proof ? "Yes" : "") || "",

    // Land taking-over conditional values (auto-populated when handing-over = Yes)
    ownLandTakingover: scrutinyMappedValues.ownLandTakingover || formValues?.ownLandTakingover || landDetails?.society || "",
    landDeedProducedTakingover:
      scrutinyMappedValues.landDeedProducedTakingover || formValues?.landDeedProducedTakingover || (landDetails?.lease_deed ? "Yes" : landDetails?.lease_deed === false ? "No" : "") || "",
    landInSameMandalTakingover: scrutinyMappedValues.landInSameMandalTakingover || formValues?.landInSameMandalTakingover || landDetails?.mandal_name || "",
    propertyScheduleTallyTakingover: scrutinyMappedValues.propertyScheduleTallyTakingover || formValues?.propertyScheduleTallyTakingover || landDetails?.survey_no || "",

    // Document-based proposed values for building fields (when existing=Yes, form shows doc icon; PDF shows text)
    planApprovedProposed: scrutinyMappedValues.planApprovedProposed || formValues?.planApprovedProposed || (hasDoc(4) ? "Document Available" : ""),
    approvedPlanUploadedProposed: scrutinyMappedValues.approvedPlanUploadedProposed || formValues?.approvedPlanUploadedProposed || (hasDoc(11) ? "Document Available" : ""),
    constructionPermissionProposed: scrutinyMappedValues.constructionPermissionProposed || formValues?.constructionPermissionProposed || (hasDoc(12) ? "Document Available" : ""),
    sanitaryCertificateProposed: scrutinyMappedValues.sanitaryCertificateProposed || formValues?.sanitaryCertificateProposed || (hasDoc(15) ? "Document Available" : ""),
    geoTaggedPhotosProposed: scrutinyMappedValues.geoTaggedPhotosProposed || formValues?.geoTaggedPhotosProposed || (hasDoc(16) ? "Document Available" : ""),
    annexureDetailsProposed: scrutinyMappedValues.annexureDetailsProposed || formValues?.annexureDetailsProposed || (hasDoc(26) ? "Document Available" : ""),
  };

  const society = Array.isArray(societyName) ? societyName[0] || {} : societyName || {};

  return {
    collCode: consolidatedFormValues.collCode || collCode,
    society,
    formValues: consolidatedFormValues,
    scrutiny,
     insertBy,
    userName,
    deficiencies:
      Array.isArray(deficiencies) && deficiencies.length > 0
        ? deficiencies
        : Array.isArray(managementPayload?.mngmtScrutiny_def)
          ? managementPayload.mngmtScrutiny_def
          : getValue(nocdata, "mngmtScrutiny_def") || [],
    accommodationRows:
      Array.isArray(accommodationRows) && accommodationRows.length > 0 ? accommodationRows : Array.isArray(managementPayload?.mngmtScrutiny_accom) ? managementPayload.mngmtScrutiny_accom : [],
    accommodationBaseRows: Array.isArray(accommodationBaseRows) ? accommodationBaseRows : [],
    corpusFundInstall: Array.isArray(corpusFundInstall) && corpusFundInstall.length > 0 ? corpusFundInstall : scrutinyInstallments,
    ugPrograms: Array.isArray(ugPrograms) && ugPrograms.length > 0 ? ugPrograms : scrutinyUgPrograms,
    pgPrograms: Array.isArray(pgPrograms) && pgPrograms.length > 0 ? pgPrograms : scrutinyPgPrograms,
    ugCorpusFunds: Array.isArray(ugCorpusFunds) && ugCorpusFunds.length > 0 ? ugCorpusFunds : scrutinyUgFunds,
    pgBefore: Array.isArray(pgBefore) && pgBefore.length > 0 ? pgBefore : scrutinyPgBeforeFunds,
    pgCorpusFunds: Array.isArray(pgCorpusFunds) && pgCorpusFunds.length > 0 ? pgCorpusFunds : scrutinyPgAfterFunds,
    nocdata,
    parking: Object.keys(parking || {}).length > 0 ? parking : scrutinyParking,
    managementDocs,
  };
};

export const openChangeOfManagementPdfInNewTab = async ({
  collCode,
  societyName,
  formValues,
  managementData,
  scrutinyDetails,
  mngmtScrutiny,
  deficiencies,
  accommodationRows,
  accommodationBaseRows,
  corpusFundInstall,
  ugPrograms,
  pgPrograms,
  ugCorpusFunds,
  pgBefore,
  pgCorpusFunds,
  nocdata,
  parking,
  previewWindow,
}) => {
  const pdfData = buildChangeOfManagementPdfData({
    collCode,
    societyName,
    formValues,
    managementData,
    scrutinyDetails,
    mngmtScrutiny,
    deficiencies,
    accommodationRows,
    accommodationBaseRows,
    corpusFundInstall,
    ugPrograms,
    pgPrograms,
    ugCorpusFunds,
    pgBefore,
    pgCorpusFunds,
    nocdata,
    parking,
  });

  const blob = await pdf(<ChangeOfManagementPdfDocument pdfData={pdfData} />).toBlob();
  const blobUrl = URL.createObjectURL(blob);

  if (previewWindow) {
    previewWindow.location.href = blobUrl;
    return blobUrl;
  }

  window.open(blobUrl, "_blank", "noopener,noreferrer");
  return blobUrl;
};

export const generateManagementDeficienciesPdf = (rowData) => {
  let content = `
        <div style="padding: 20px; font-family: Arial;">

            <h2 style="text-align: center; color: green;">
                ${rowData.inst_name}
            </h2>

            <h2 style="text-align: center;">
                Management Scrutiny - Deficiencies Report
            </h2>

            <p><b>College Code:</b> ${rowData.coll_code}</p>
            <p><b>NOC Type:</b> ${rowData.noc_type || "-"}</p>
            ${rowData.final_status ? `<p><b>Final Status:</b> ${rowData.final_status}</p>` : ""}
            ${rowData.final_remarks ? `<p><b>Final Remarks:</b> ${rowData.final_remarks}</p>` : ""}

            <table style="width: 100%; border-collapse: collapse; margin-top: 20px;">
                <thead>
                    <tr style="background-color: #f2f2f2;">
                        <th style="border: 1px solid black; padding: 8px; width: 10%;">
                            S.No
                        </th>
                        <th style="border: 1px solid black; padding: 8px; width: 90%;">
                            Deficiency
                        </th>
                    </tr>
                </thead>
                <tbody>
    `;

  (rowData.deficiencies || []).forEach((d, index) => {
    content += `
            <tr>
                <td style="border: 1px solid black; padding: 8px; text-align: center;">
                    ${index + 1}
                </td>
                <td style="border: 1px solid black; padding: 8px;">
                    ${d.remarks || "-"}
                </td>
            </tr>
        `;
  });

  content += `
                </tbody>
            </table>
        </div>
    `;

  const element = document.createElement("div");
  element.innerHTML = content;

  html2pdf()
    .set({
      margin: 10,
      filename: `Management_Deficiencies_${rowData.coll_code}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .from(element)
    .save();
};
